import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Feedback, PointerActivationConstraints } from '@dnd-kit/dom';
import {
  DragDropProvider,
  PointerSensor,
  type DragEndEvent as ReactDragEndEvent,
  type DragOverEvent as ReactDragOverEvent,
  type DragStartEvent as ReactDragStartEvent,
  useDroppable,
} from '@dnd-kit/react';

import { isCollectionBlock } from '@/lib/blocks/collection/blocks';
import {
  blockOrDescendantsForceFullWidth,
  getBlockDisplayWidthStyle,
} from '@/lib/blocks/layout/block-layout';
import {
  BLOCK_STACK_ALIGN_DEFAULT,
  BLOCK_STACK_DIRECTION_DEFAULT,
  type BlockStackLayoutConfig,
  getBlockStackContainerClassName,
  getBlockStackTextAlignClassName,
  getTopLevelBlockWrapperClassName,
  shouldStretchTopLevelBlockWrapper,
} from '@/lib/blocks/layout/block-stack-layout';
import type { GroupDirection } from '@/lib/blocks/layout/group-layout';
import {
  buildBlockTree,
  buildStableBlockTree,
  collectDescendantIds,
  collectDescendantStableIds,
} from '@/lib/blocks/tree';
import { addWindowEventListener, cn, getDocument } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import {
  type BlockStructuralRegion,
  getBlockUiKey,
  isParentBlockType,
  type PageBlock,
} from '@/types/block';
import { SectionGridRenderer } from '../renderers/section-grid-renderer';
import { useBlockSelectionStore } from './block-selection';
import { resetDynamicCollisionSession } from './dnd/collision/dynamic-detector';
import {
  DRAG_START_DISTANCE_PX,
  type DropIndicator,
  getEventActivator,
  getEventActiveId,
  getEventOver,
  isMouseActivatorEvent,
  isTouchActivatorEvent,
  normalizeBlocks,
  ROOT_PARENT_DROP_ID,
  resolveDropResult,
} from './dnd/core';
import { computeDragOverDecision } from './dnd/decision/drag-over-decision';
import { DND_ACTIVE_SCOPE_CLASS, getRootDropIndicatorClassName } from './dnd/drag-visuals';
import {
  handleArrowReorder,
  handleDeleteSelection,
  isTypingFocusElement,
  resolveParentSelectionShortcut,
  shouldCancelDragOnEscape,
} from './dnd/keyboard/actions';
import { isEditorCollectionItemDragEvent } from './dnd/resolution/event-adapters';
import { resolveEffectiveOverForDragEnd } from './dnd/state/grid-state';
import { SortableLeafBlock } from './sortable-leaf-block';
import { SortableParentBlock } from './sortable-parent-block';

// Editor-side rendering for block-tree DnD:
// - renders sortable blocks/groups
// - renders invisible edge drop zones
// - turns active droppable metadata into a single drop indicator

interface BlockEditorGridProps {
  sectionId: string;
  editorSectionId?: string;
  blocks: PageBlock[];
  stackLayout?: BlockStackLayoutConfig;
  isLocked?: boolean;
  /** Optional bounded subtrees. When present, structure outside these regions is locked. */
  structuralRegions?: readonly BlockStructuralRegion[];
}

function RootParentDropZone({
  dropIndicator,
  rootParentDirection,
  disabled = false,
}: {
  dropIndicator: DropIndicator | null;
  rootParentDirection: GroupDirection;
  disabled?: boolean;
}) {
  // Hidden top-level append target. Keeps UI clean while still allowing "move back to root".
  const { ref } = useDroppable({
    id: ROOT_PARENT_DROP_ID,
    data: { type: 'parent-drop', parentId: null },
    disabled,
  });
  const showIndicator = dropIndicator?.targetId === ROOT_PARENT_DROP_ID;

  return (
    <div ref={ref} className="absolute inset-x-0 bottom-0 h-1">
      {showIndicator && <div className={getRootDropIndicatorClassName(rootParentDirection)} />}
    </div>
  );
}

export function BlockEditorGrid({
  sectionId,
  editorSectionId,
  blocks,
  stackLayout,
  isLocked = false,
  structuralRegions,
}: BlockEditorGridProps) {
  const editorBoundsRef = useRef<HTMLDivElement | null>(null);
  const updateBlocks = usePageEditorStore((state) => state.updateBlocks);
  const deleteBlock = usePageEditorStore((state) => state.deleteBlock);
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const dropIndicatorRef = useRef<DropIndicator | null>(null);
  const LAST_KNOWN_OVER_MAX_AGE_MS = 200;
  const lastKnownOverRef = useRef<{
    over: ReturnType<typeof getEventOver>;
    timestampMs: number | null;
  }>({
    over: null,
    timestampMs: null,
  });
  const [activeDragBlockId, setActiveDragBlockId] = useState<string | null>(null);
  const keyboardIndicatorTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const editorSectionUiKey = editorSectionId ?? sectionId;
  const clearBlockState = useBlockSelectionStore((state) => state.clearBlockState);
  const setActiveBlock = useBlockSelectionStore((state) => state.setActiveBlock);
  const isOverlayFocused = useBlockSelectionStore((state) => state.overlayFocused);
  const activeSelection = useBlockSelectionStore((state) =>
    state.active?.sectionId === editorSectionUiKey ? state.active : null,
  );

  const sourceBlockMap = useMemo(
    () => new Map(blocks.map((block) => [getBlockUiKey(block), block])),
    [blocks],
  );
  const stableIdByPersistedId = useMemo(
    () => new Map(blocks.map((block) => [block.id, getBlockUiKey(block)])),
    [blocks],
  );
  const blockMapById = useMemo(() => new Map(blocks.map((block) => [block.id, block])), [blocks]);
  const editorTree = useMemo(() => buildStableBlockTree(blocks), [blocks]);
  const { topLevel, childrenByParent } = editorTree;
  const structuralPolicy = useMemo(() => {
    if (structuralRegions === undefined) return null;

    const editableBlockIds = new Set<string>();
    const regionRootByBlockId = new Map<string, string>();
    const protectedBlockIds = new Set<string>();
    const regionByRootId = new Map<string, BlockStructuralRegion>();
    const toUiKey = (id: string) => stableIdByPersistedId.get(id) ?? id;

    structuralRegions.forEach((region) => {
      const rootUiKey = toUiKey(region.rootBlockId);
      if (!sourceBlockMap.has(rootUiKey)) return;

      regionByRootId.set(rootUiKey, region);
      const regionIds = new Set([
        rootUiKey,
        ...collectDescendantStableIds(rootUiKey, childrenByParent),
      ]);

      regionIds.forEach((id) => {
        editableBlockIds.add(id);
        regionRootByBlockId.set(id, rootUiKey);
      });
      region.protectedBlockIds.forEach((id) => {
        protectedBlockIds.add(toUiKey(id));
      });
    });

    return {
      editableBlockIds,
      regionRootByBlockId,
      protectedBlockIds,
      regionByRootId,
    };
  }, [childrenByParent, sourceBlockMap, stableIdByPersistedId, structuralRegions]);

  const canDeleteStableBlock = useCallback(
    (blockUiKey: string) => {
      if (!structuralPolicy) return true;
      return (
        structuralPolicy.editableBlockIds.has(blockUiKey) &&
        !structuralPolicy.protectedBlockIds.has(blockUiKey)
      );
    },
    [structuralPolicy],
  );

  useEffect(() => {
    return () => {
      if (keyboardIndicatorTimerRef.current !== null) {
        globalThis.clearTimeout(keyboardIndicatorTimerRef.current);
      }
    };
  }, []);

  const applyBlocks = useCallback(
    (nextBlocks: PageBlock[]) => {
      // Always normalize before persisting editor state so tree/spans stay deterministic.
      const normalizedBlocks = normalizeBlocks(
        nextBlocks,
        stackLayout?.direction ?? BLOCK_STACK_DIRECTION_DEFAULT,
      );

      if (structuralRegions === undefined) {
        updateBlocks(sectionId, normalizedBlocks);
        return;
      }

      const { childrenByParent: nextChildrenByParent } = buildBlockTree(nextBlocks);
      const editableBlockIds = new Set<string>();
      structuralRegions.forEach((region) => {
        collectDescendantIds(region.rootBlockId, nextChildrenByParent).forEach((blockId) => {
          editableBlockIds.add(blockId);
        });
      });
      const normalizedBlockById = new Map(normalizedBlocks.map((block) => [block.id, block]));

      updateBlocks(
        sectionId,
        nextBlocks.map((block) =>
          editableBlockIds.has(block.id) ? (normalizedBlockById.get(block.id) ?? block) : block,
        ),
      );
    },
    [sectionId, stackLayout?.direction, structuralRegions, updateBlocks],
  );

  const setIndicator = useCallback((nextIndicator: DropIndicator | null) => {
    const currentIndicator = dropIndicatorRef.current;
    const indicatorUnchanged =
      nextIndicator?.targetId === currentIndicator?.targetId &&
      nextIndicator?.edge === currentIndicator?.edge;
    if (indicatorUnchanged) {
      return;
    }
    // Ref is the authoritative source for drag-end resolution.
    dropIndicatorRef.current = nextIndicator;
    setDropIndicator(nextIndicator);
  }, []);

  const handleDragOver = useCallback(
    (event: ReactDragOverEvent) => {
      if (isEditorCollectionItemDragEvent(event)) {
        return;
      }
      const over = getEventOver(event);
      if (over) {
        lastKnownOverRef.current = {
          over,
          timestampMs: Date.now(),
        };
      }
      const previousIndicator = dropIndicatorRef.current;
      const decision = computeDragOverDecision({
        event,
        blocks,
        blockMap: sourceBlockMap,
        stableIdByPersistedId,
        childrenByParent,
        rootParentDirection: stackLayout?.direction ?? BLOCK_STACK_DIRECTION_DEFAULT,
        rootStackLayout: stackLayout,
        previousIndicator,
        structuralRegionRootByBlockId: structuralPolicy?.regionRootByBlockId,
        protectedBlockIds: structuralPolicy?.protectedBlockIds,
      });
      const nextIndicator = decision.indicator;
      setIndicator(nextIndicator);
    },
    [
      blocks,
      setIndicator,
      sourceBlockMap,
      stableIdByPersistedId,
      childrenByParent,
      stackLayout,
      structuralPolicy,
    ],
  );

  const handleDragStart = useCallback(
    ({ id }: { id: string }) => {
      resetDynamicCollisionSession(id);
      clearBlockState();
      setActiveDragBlockId(id);
    },
    [clearBlockState],
  );

  const resetDragState = useCallback(() => {
    resetDynamicCollisionSession();
    lastKnownOverRef.current = { over: null, timestampMs: null };
    setActiveDragBlockId(null);
    setIndicator(null);
  }, [setIndicator]);

  const handleDragEnd = useCallback(
    (event: ReactDragEndEvent) => {
      const latestDropIndicator = dropIndicatorRef.current;
      const eventOver = getEventOver(event);
      const getDragEndClientPoint = (): { x: number; y: number } | null => {
        const activatorEvent = getEventActivator(event);
        if (isMouseActivatorEvent(activatorEvent)) {
          return {
            x: activatorEvent.clientX,
            y: activatorEvent.clientY,
          };
        }
        if (isTouchActivatorEvent(activatorEvent)) {
          const touch = activatorEvent.changedTouches[0] ?? activatorEvent.touches[0];
          if (touch) {
            return { x: touch.clientX, y: touch.clientY };
          }
        }
        return null;
      };
      const effectiveOver = resolveEffectiveOverForDragEnd({
        eventOver,
        lastKnownOver: lastKnownOverRef.current.over,
        lastKnownOverTimestampMs: lastKnownOverRef.current.timestampMs,
        now: Date.now(),
        maxLastKnownOverAgeMs: LAST_KNOWN_OVER_MAX_AGE_MS,
        latestDropIndicator,
        activeStableId: getEventActiveId(event),
        sourceBlockMap,
        stableIdByPersistedId,
        editorBounds: editorBoundsRef.current?.getBoundingClientRect() ?? null,
        dragEndClientPoint: getDragEndClientPoint(),
      });
      // Resolve drop into an updated block array using shared tree logic.
      const nextBlocks = resolveDropResult({
        event,
        blocks,
        blockMap: sourceBlockMap,
        childrenByParent,
        dropIndicator: latestDropIndicator,
        fallbackOver: effectiveOver,
        rootParentDirection: stackLayout?.direction ?? BLOCK_STACK_DIRECTION_DEFAULT,
        rootStackLayout: stackLayout,
        structuralRegionRootByBlockId: structuralPolicy?.regionRootByBlockId,
        protectedBlockIds: structuralPolicy?.protectedBlockIds,
      });

      if (nextBlocks) {
        applyBlocks(nextBlocks);
      }
    },
    [
      applyBlocks,
      blocks,
      sourceBlockMap,
      childrenByParent,
      stableIdByPersistedId,
      stackLayout,
      structuralPolicy,
    ],
  );

  const handleDragEndWithReset = useCallback(
    (event: ReactDragEndEvent) => {
      if (isEditorCollectionItemDragEvent(event)) {
        resetDragState();
        return;
      }
      const draggedBlockId = getEventActiveId(event);
      handleDragEnd(event);
      resetDragState();
      setActiveBlock(editorSectionUiKey, draggedBlockId, { syncHover: true });
    },
    [handleDragEnd, resetDragState, setActiveBlock, editorSectionUiKey],
  );

  const handleUpdateGroupProps = useCallback(
    (groupBlockId: string, patch: Record<string, unknown>) => {
      const groupBlock = blockMapById.get(groupBlockId);
      if (!groupBlock || groupBlock.blockType !== 'group') {
        return;
      }

      const nextProps = { ...(groupBlock.props || {}), ...patch };
      if ('float' in patch) {
        updateBlock(sectionId, groupBlockId, { props: nextProps });
        return;
      }

      const nextBlocks = blocks.map((block) => {
        if (block.id !== groupBlockId) {
          return block;
        }

        return {
          ...block,
          props: nextProps,
        };
      });

      applyBlocks(nextBlocks);
    },
    [applyBlocks, blockMapById, blocks, sectionId, updateBlock],
  );

  useEffect(() => {
    if (isLocked) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        shouldCancelDragOnEscape({
          key: event.key,
          isDragSessionActive: activeDragBlockId !== null,
        })
      ) {
        event.preventDefault();
        resetDragState();
        return;
      }

      const activeElement = getDocument()?.activeElement as HTMLElement | null;
      const isTypingFocus = isTypingFocusElement(activeElement);

      if (event.defaultPrevented || event.altKey || isOverlayFocused) {
        return;
      }
      const deleteAction = handleDeleteSelection({
        key: event.key,
        isTypingFocus,
        activeSelectionStableId: activeSelection?.blockId ?? null,
        blockMap: sourceBlockMap,
      });
      if (deleteAction && canDeleteStableBlock(activeSelection?.blockId ?? '')) {
        event.preventDefault();
        deleteBlock(sectionId, deleteAction.blockId, {
          preserveEmptyParentIds: structuralPolicy?.protectedBlockIds,
        });
        return;
      }

      const parentSelectionAction = resolveParentSelectionShortcut({
        event,
        isTypingFocus,
        activeSelectionStableId: activeSelection?.blockId ?? null,
        blockMap: sourceBlockMap,
      });
      if (parentSelectionAction) {
        event.preventDefault();
        setActiveBlock(editorSectionUiKey, parentSelectionAction.parentStableId, {
          syncHover: true,
        });
        return;
      }

      const activeStructuralEditAllowed = activeSelection?.blockId
        ? !structuralPolicy ||
          (structuralPolicy.editableBlockIds.has(activeSelection.blockId) &&
            !structuralPolicy.protectedBlockIds.has(activeSelection.blockId))
        : false;
      const arrowAction = activeStructuralEditAllowed
        ? handleArrowReorder({
            key: event.key,
            isTypingFocus,
            activeSelectionStableId: activeSelection?.blockId ?? null,
            blocks,
            blockMap: sourceBlockMap,
            childrenByParent,
          })
        : null;
      if (!arrowAction) {
        setIndicator(null);
        return;
      }

      event.preventDefault();
      setIndicator(arrowAction.indicator);
      applyBlocks(arrowAction.nextBlocks);
      if (keyboardIndicatorTimerRef.current !== null) {
        globalThis.clearTimeout(keyboardIndicatorTimerRef.current);
      }
      keyboardIndicatorTimerRef.current = globalThis.setTimeout(() => {
        setIndicator(null);
        keyboardIndicatorTimerRef.current = null;
      }, 180);
      setActiveBlock(editorSectionUiKey, arrowAction.movedStableId, { syncHover: true });
    };

    const remove = addWindowEventListener<KeyboardEvent>('keydown', onKeyDown);
    return () => {
      remove?.();
    };
  }, [
    activeSelection,
    applyBlocks,
    blocks,
    canDeleteStableBlock,
    deleteBlock,
    isOverlayFocused,
    isLocked,
    setActiveBlock,
    setIndicator,
    sourceBlockMap,
    childrenByParent,
    activeDragBlockId,
    resetDragState,
    editorSectionUiKey,
    sectionId,
    structuralPolicy,
  ]);

  if (isLocked) {
    // Locked sections use display renderer only (no sortable wrappers/drop zones).
    return <SectionGridRenderer blocks={blocks} showHidden stackLayout={stackLayout} />;
  }

  const activeDragBlock = activeDragBlockId ? sourceBlockMap.get(activeDragBlockId) : undefined;
  const isDragSessionActive = activeDragBlockId !== null;
  const activeDragParentUiKey = activeDragBlock?.parentBlockId
    ? (() => {
        const directParent = blockMapById.get(activeDragBlock.parentBlockId as string);
        return directParent ? getBlockUiKey(directParent) : activeDragBlock.parentBlockId;
      })()
    : null;
  let allowedScopeParentId: string | null | undefined;
  if (activeDragBlock) {
    const activeRegionRootId = structuralPolicy?.regionRootByBlockId.get(activeDragBlockId ?? '');
    if (activeRegionRootId) {
      allowedScopeParentId = activeRegionRootId;
    }
    const activeParent = activeDragParentUiKey
      ? sourceBlockMap.get(activeDragParentUiKey)
      : undefined;
    if (allowedScopeParentId === undefined && activeParent && isCollectionBlock(activeParent)) {
      allowedScopeParentId = activeDragParentUiKey;
    }
  }
  const isHorizontalStack = stackLayout?.direction === 'horizontal';
  const rootParentDirection = stackLayout?.direction ?? BLOCK_STACK_DIRECTION_DEFAULT;
  const rootContentAlign = stackLayout?.align ?? BLOCK_STACK_ALIGN_DEFAULT;
  const topLevelTextAlignClassName = getBlockStackTextAlignClassName(stackLayout);
  const shouldStretchStackContainer = topLevel.some((block) =>
    shouldStretchTopLevelBlockWrapper({
      block,
      groupChildren: childrenByParent.get(getBlockUiKey(block)) ?? [],
      topLevelCount: topLevel.length,
      isHorizontalStack,
    }),
  );
  return (
    <DragDropProvider
      sensors={() => [
        PointerSensor.configure({
          activationConstraints: [
            new PointerActivationConstraints.Distance({
              value: DRAG_START_DISTANCE_PX,
            }),
          ],
        }),
      ]}
      plugins={(defaults) => [
        ...defaults,
        Feedback.configure({ feedback: 'clone', dropAnimation: null }),
      ]}
      onDragStart={(event: ReactDragStartEvent) => {
        if (isEditorCollectionItemDragEvent(event)) {
          return;
        }
        handleDragStart({
          id: String(event.operation.source?.id ?? ''),
        });
      }}
      onDragOver={(event: ReactDragOverEvent) => handleDragOver(event)}
      onDragEnd={(event: ReactDragEndEvent) => handleDragEndWithReset(event)}
    >
      <div
        ref={editorBoundsRef}
        className={cn(
          'relative',
          getBlockStackContainerClassName(stackLayout),
          shouldStretchStackContainer && 'h-full flex-1',
          isDragSessionActive && allowedScopeParentId === null && DND_ACTIVE_SCOPE_CLASS,
        )}
      >
        {topLevel.map((block, index) => {
          return (
            <div
              key={getBlockUiKey(block)}
              data-block-type={block.blockType}
              className={cn(
                getTopLevelBlockWrapperClassName({
                  block,
                  groupChildren: childrenByParent.get(getBlockUiKey(block)) ?? [],
                  topLevelCount: topLevel.length,
                  isHorizontalStack,
                  stackAlign: stackLayout?.align,
                  stackPadding: stackLayout?.padding,
                  topLevelTextAlignClassName,
                  isLastBlock: index === topLevel.length - 1,
                  hasForceFullWidthDescendant: blockOrDescendantsForceFullWidth(
                    block,
                    (b) => childrenByParent.get(getBlockUiKey(b)) || [],
                  ),
                }),
                isDragSessionActive &&
                  'transition-[transform,margin,opacity] duration-200 ease-out',
              )}
              style={getBlockDisplayWidthStyle(block)}
            >
              {isParentBlockType(block.blockType) ? (
                <SortableParentBlock
                  sectionId={sectionId}
                  editorSectionId={editorSectionUiKey}
                  block={block}
                  parentId={null}
                  parentStableId={null}
                  parentDirection={rootParentDirection}
                  childrenByParent={childrenByParent}
                  dropIndicator={dropIndicator}
                  onUpdateGroupProps={handleUpdateGroupProps}
                  isDragSessionActive={isDragSessionActive}
                  allowedScopeParentId={allowedScopeParentId}
                  structuralEditableBlockIds={structuralPolicy?.editableBlockIds}
                  protectedBlockIds={structuralPolicy?.protectedBlockIds}
                  structuralRegionByRootId={structuralPolicy?.regionByRootId}
                />
              ) : (
                <SortableLeafBlock
                  sectionId={sectionId}
                  editorSectionId={editorSectionUiKey}
                  block={block}
                  parentStableId={null}
                  parentDirection={rootParentDirection}
                  contentAlign={rootContentAlign}
                  dropIndicator={dropIndicator}
                  isDragSessionActive={isDragSessionActive}
                  structuralEditableBlockIds={structuralPolicy?.editableBlockIds}
                  protectedBlockIds={structuralPolicy?.protectedBlockIds}
                />
              )}
            </div>
          );
        })}
        {!isHorizontalStack ? (
          <RootParentDropZone
            dropIndicator={dropIndicator}
            rootParentDirection={stackLayout?.direction ?? BLOCK_STACK_DIRECTION_DEFAULT}
            disabled={structuralPolicy !== null}
          />
        ) : null}
      </div>
    </DragDropProvider>
  );
}
